<template>
  <!-- eslint-disable vue/no-mutating-props -->
  <section class="flex flex-col gap-5 border-x border-b rounded-b mb-7 w-full p-4 z-[1] bg-white">
    <section class="relative">
      <h2>
        {{ name }}
      </h2>
      <DsButton
        v-show="!form.wurdeGeladen"
        icon="xmark-large"
        icon-variant="regular"
        variant="clear"
        :disabled="form.wurdeGeladen"
        title="entfernen"
        class="absolute top-0 right-0"
        @click="$emit('delete', index)"
      />
    </section>

    <section class="flex flex-col gap-5 w-full lg:w-2/3 mx-auto mb-0">
      <h5 class="mb-2">
        Mein Kind ist
      </h5>
      <DsFieldError
        v-for="error in formErrors.anrede"
        :key="error"
      >
        {{ error }}
      </DsFieldError>

      <OptionGroup
        :id="`formError.kinder.${[index]}.anrede`"
        has-icons
        :options="[
          {value: 2, label: 'weiblich', icon: 'icomoon/female',
           labelClasses: 'flex flex-col', iconClass: 'gender'},
          {value: 1, label: 'männlich', icon: 'icomoon/male',
           labelClasses: 'flex flex-col', iconClass: 'gender'},
        ]"
        :value="form.anrede"
        option-width-classes="w-1/3 md:w-1/2"
        @change="set('anrede', $event)"
      />

      <div class="flex flex-col gap-5">
        <DsFormGroup
          :id="`formError.kinder.${[index]}.vorname`"
          label="Vorname"
          label-for="Vorname"
        >
          <DsInput
            id="Vorname"
            :model-value="form.vorname"
            size="lg"
            immediate
            @update:model-value="set('vorname', $event)"
          />
          <DsFieldError
            v-for="error in formErrors.vorname"
            :key="error"
          >
            {{ error }}
          </DsFieldError>
        </DsFormGroup>
        <DsFormGroup
          :id="`formError.kinder.${[index]}.nachname`"
          label="Nachname"
          label-for="Nachname"
        >
          <DsInput
            id="Nachname"
            :model-value="form.nachname"
            size="lg"
            immediate
            @update:model-value="set('nachname', $event)"
          />
          <DsFieldError
            v-for="error in formErrors.nachname"
            :key="error"
          >
            {{ error }}
          </DsFieldError>
        </DsFormGroup>
        <DsFormGroup
          :id="`formError.kinder.${[index]}.geburtsdatum`"
          label="Geburtsdatum"
          label-for="Geburtsdatum"
        >
          <DateInput
            id="Geburtsdatum"
            :model-value="form.geburtsdatum"
            size="lg"
            @update:model-value="set('geburtsdatum', $event)"
          />
          <DsFieldError
            v-for="error in formErrors.geburtsdatum"
            :key="error"
          >
            {{ error }}
          </DsFieldError>
        </DsFormGroup>
      </div>
    </section>

    <section
      v-if="isSixteen"
      class="flex flex-col gap-5 w-full lg:w-2/3 mx-auto mt-7"
    >
      <h5 class="-mb-3">
        Berufsstatus und Einkommen meines Kindes
      </h5>
      <DsFormGroup label="Berufsstatus" label-for="Berufsstatus">
        <DsSelect
          id="Berufsstatus"
          :model-value="form.taetigkeitsstatus"
          :data="taetigkeitsstatus"
          placeholder=""
          size="lg"
          @update:model-value="set('taetigkeitsstatus', $event)"
        />
        <DsFieldError
          v-for="error in formErrors.taetigkeitsstatus"
          :key="error"
        >
          {{ error }}
        </DsFieldError>
      </DsFormGroup>
      <DsFormGroup label="Bruttoeinkommen">
        <MoneyInput
          :model-value="form.brutto"
          size="lg"
          as-string
          @update:model-value="set('brutto', handleMoneyValue($event))"
        />
        <DsFieldError
          v-for="error in formErrors.brutto"
          :key="error"
        >
          {{ error }}
        </DsFieldError>
      </DsFormGroup>

      <DsRadioGroup
        :model-value="form.isBruttoJahresGehalt === null ? '' : form.isBruttoJahresGehalt ? '1' : '0'"
        :variant="isMobile ? 'button-col' : 'button'"
        class="w-full bg-white"
        @update:model-value="form.isBruttoJahresGehalt = $event === '1'"
      >
        <DsRadioButton
          value="1"
          style="height: 48px"
        >
          im Jahr
        </DsRadioButton>

        <DsRadioButton
          value="0"
          style="height: 48px"
        >
          im Monat
        </DsRadioButton>
      </DsRadioGroup>
      <DsFormGroup label="Nettoeinkommen">
        <MoneyInput
          :model-value="form.netto"
          size="lg"
          as-string
          @update:model-value="set('netto', handleMoneyValue($event))"
        />
        <DsFieldError
          v-for="error in formErrors.netto"
          :key="error"
        >
          {{ error }}
        </DsFieldError>
      </DsFormGroup>

      <DsRadioGroup
        :model-value="form.isNettoJahresGehalt === null ? '' : form.isNettoJahresGehalt ? '1' : '0'"
        :variant="isMobile ? 'button-col' : 'button'"
        class="w-full bg-white"
        @update:model-value="form.isNettoJahresGehalt = $event === '1'"
      >
        <DsRadioButton
          value="1"
          style="height: 48px"
        >
          im Jahr
        </DsRadioButton>

        <DsRadioButton
          value="0"
          style="height: 48px"
        >
          im Monat
        </DsRadioButton>
      </DsRadioGroup>
    </section>

    <section class="flex flex-col gap-5 w-full lg:w-2/3 mx-auto mt-7">
      <h5 class="mb-5">
        Der Krankenversicherungsstatus meines Kindes lautet
      </h5>
      <DsFieldError
        v-for="error in formErrors.krankenversicherungsstatus"
        :key="error"
      >
        {{ error }}
      </DsFieldError>
      <DsRadioGroup
        :model-value="form.krankenversicherungsstatus === null ? '' : `${form.krankenversicherungsstatus}`"
        variant="button-col"
        class="w-full bg-white"
        @update:model-value="form.krankenversicherungsstatus = $event === '' ? null : Number($event)"
      >
        <DsRadioButton
          v-for="{label, value} in krankenversicherungsstatus"
          :key="value"
          :value="`${value}`"
          style="height: 48px"
        >
          {{ label }}
        </DsRadioButton>
      </DsRadioGroup>
    </section>

    <section class="flex flex-col gap-5 w-full lg:w-2/3 mx-auto mt-7">
      <h5 class="mb-5">
        Mein Kind leidet an einer schweren Krankheit
        <DsButton
          class="ml-2"
          variant="outline"
          icon="info"
          size="sm"
          title="Weitere Infos anzeigen"
          @click="showModal = true"
        />
      </h5>
      <DsFieldError
        v-for="error in formErrors.hatSchwereKrankheit"
        :key="error"
      >
        {{ error }}
      </DsFieldError>

      <OptionGroup
        :id="`formError.kinder.${[index]}.hatSchwereKrankheit`"
        has-icons
        :options="[
          {label: 'Ja', labelClasses: `${form.hatSchwereKrankheit === 1 ? 'text-green-700' : 'flex flex-col'}`, value: 1, icon: 'icomoon/check', iconClass: `check ${form.hatSchwereKrankheit === 1 ? 'fill-green-700' : ''}`},
          {label: 'Nein', labelClasses: `${form.hatSchwereKrankheit === 0 ? 'text-red-700' : 'flex flex-col'}`, value: 0, icon: 'icomoon/close', iconClass: `close ${form.hatSchwereKrankheit === 0 ? 'fill-red-700' : ''}`}
        ]"
        :value="form.hatSchwereKrankheit"
        option-width-classes="w-1/3"
        @change="set('hatSchwereKrankheit', $event)"
      />

      <div v-if="form.hatSchwereKrankheit">
        <DsFormGroup label="Diagnose" label-for="Diagnose">
          <DsTextarea
            id="Diagnose"
            :model-value="form.diagnose === null ? undefined : form.diagnose"
            :input-id="`kind_${index}_diagnose`"
            :name="`kind_${index}_diagnose`"
            :placeholder="`Bitte ${duzen('gebe hier deine', 'geben Sie hier Ihre')} Diagnose ein.`"
            size="lg"
            :rows="8"
            @update:model-value="form.diagnose = $event === undefined ? null : $event"
          />
          <DsFieldError
            v-for="error in formErrors.diagnose"
            :key="error"
          >
            {{ error }}
          </DsFieldError>
        </DsFormGroup>
      </div>
    </section>

    <SchwereKrankheitenModal v-model:show="showModal" />
  </section>
</template>

<script setup lang="ts">
import {
  DsButton,
  DsFieldError,
  DsFormGroup,
  DsInput,
  DsRadioButton,
  DsRadioGroup,
  DsSelect,
  DsTextarea
} from '@demvsystems/design-components';
import { differenceInYears, parse } from 'date-fns';
import { sortBy } from 'lodash-es';
import { computed, ref } from 'vue';

import DateInput from '@/application/components/form-elements/DateInput/DateInput.vue';
import MoneyInput from '@/application/components/form-elements/MoneyInput/MoneyInput.vue';
import { useDetectMobile } from '@/application/composables/detectMobile';
import OptionGroup from '@/checkup/components/OptionGroup.vue';
import SchwereKrankheitenModal from '@/checkup/components/steps/SchwereKrankheitenModal.vue';
import { toLabelAndValue } from '@/checkup/utils/demvWerte';
import { demvWerte } from '@/checkup/state';
import type { Kind } from '@/checkup/types/types';
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';

interface Props {
  index: number;
  form: Kind;
  formErrors?: object;
}

const props = withDefaults(defineProps<Props>(), {
  formErrors: () => ({}),
})
const emit = defineEmits(['delete', 'clear'])

const { isMobile } = useDetectMobile();
const { duzen } = useBaseDataStore();

const showModal = ref(false);

const name = computed(() => (props.form.vorname
  ? `${props.form.vorname} ${props.form.nachname || ''}`.trim()
  : `Kind ${Number(props.index) + 1}`)
);

const isSixteen = computed(() => {
  if (!props.form.geburtsdatum) return false;

  const now = new Date();
  const input = parse(props.form.geburtsdatum, 'dd.MM.yyyy', now);
  const age = differenceInYears(now, input);

  return !Number.isNaN(age) && age >= 16;
})

const taetigkeitsstatus = sortBy(toLabelAndValue(demvWerte.taetigkeitstatus), 'label');
const krankenversicherungsstatus = toLabelAndValue(demvWerte.krankenversicherungsstatus);

const set = (field: string, value: number | string | boolean | null) => {
  // eslint-disable-next-line vue/no-mutating-props
  props.form[field] = value;
  emit('clear', `${field}`);
};

// TODO: remove when backend accepts value as float
function handleMoneyValue(value: number | string | null): number | string | null {
  if (value === null || value === '0' || value === 0) return null;
  if (typeof value === 'number') return value.toString();
  return value;
}
</script>
